// Frontend mirror of the backend credit costs and plan metadata.
//
// KEEP THIS IN SYNC WITH THE BACKEND REGISTRY (Track 2's ACTION_COSTS in
// src/credits/*). The backend is the source of truth at charge time —
// these values are only used for UX gating ("is this button affordable?")
// and the pre-action label ("Build deck · 40 credits"). A drift of 1-2
// credits is a cosmetic bug, not a correctness bug: the server will
// always reject or debit the real amount.

export const ACTION_COSTS = {
  build_deck: 40,
  upload_deck: 25,
  draft_build: 40, // Limited builder from an uploaded pool; priced at build_deck parity
  regenerate_strategy: 25, // Single Opus pass to rewrite analysis prose
  playtest: 0, // Forge sim is free; LLM summary is where we charge
  ask_deck: 15,
  match_summary: 10,
  gauntlet: 0, // Forge sim only — same policy as playtest
  gauntlet_summary: 20, // On-demand editor's brief (3 match summaries + rollup)
} as const;

export type PlanId = "free" | "starter" | "pro" | "max";

export interface PlanMeta {
  id: PlanId;
  name: string;
  price_usd: number | null; // null = free
  monthly_grant: number;
}

export const PLANS: Record<PlanId, PlanMeta> = {
  free: { id: "free", name: "Free", price_usd: null, monthly_grant: 100 },
  starter: { id: "starter", name: "Starter", price_usd: 10, monthly_grant: 600 },
  pro: { id: "pro", name: "Legendary", price_usd: 20, monthly_grant: 1300 },
  max: { id: "max", name: "Planeswalker", price_usd: 40, monthly_grant: 3000 },
};

export function planLabel(id: PlanId): string {
  return PLANS[id]?.name ?? id;
}

export function monthlyGrantFor(id: PlanId): number {
  return PLANS[id]?.monthly_grant ?? 100;
}

// "What can I do" hints for the sidebar widget. We deliberately show
// only the two flagship actions (build + summary) — four rows would
// crowd a 240px-wide sidebar. Playtest sims themselves are free so
// we show match_summary (the billed part) instead.
export function affordances(total: number): { builds: number; summaries: number } {
  return {
    builds: Math.floor(total / ACTION_COSTS.build_deck),
    summaries: Math.floor(total / ACTION_COSTS.match_summary),
  };
}

export function canAfford(total: number, cost: number): boolean {
  return total >= cost;
}
